// feedback.jsx — Wave 1: the single preferred application-feedback system.
//
// Two DIFFERENT concerns, deliberately separated:
//   1. Application feedback (this module): result of a user action — saved,
//      rejected, invalid input, network error. Use `useWavyFeedback()` or the
//      `FeedbackProvider` API in all NEW work.
//   2. Realtime document notifications (SystemRealtimeToast + NotificationBell):
//      Socket.IO-driven incoming workflow events. NOT touched by this module —
//      SystemRealtimeToast keeps functioning exactly as before.
//
// Legacy `alert()`/`confirm()` and react-toastify remain in existing pages;
// they migrate progressively in later waves. New code must not add more.

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from "lucide-react";

const TONE_ICONS = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
};

const FeedbackContext = createContext(null);

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    (message, { tone = "info", duration = 4200 } = {}) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-4), { id, message: String(message ?? ""), tone }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      notify,
      dismiss,
      success: (message, opts) => notify(message, { tone: "success", ...opts }),
      error: (message, opts) => notify(message, { tone: "danger", duration: 6000, ...opts }),
      warning: (message, opts) => notify(message, { tone: "warning", ...opts }),
      info: (message, opts) => notify(message, { tone: "info", ...opts }),
    }),
    [notify, dismiss]
  );

  return (
    <FeedbackContext.Provider value={api}>
      {children}
      <div className="wv-toast-viewport" aria-live="polite" aria-label="Application notifications">
        {toasts.map(({ id, message, tone }) => {
          const Icon = TONE_ICONS[tone] || Info;
          return (
            <div key={id} className={`wv-toast wv-toast--${tone}`}>
              <Icon size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: `var(--wv-${tone === "danger" ? "danger" : tone === "warning" ? "warning" : tone === "success" ? "success" : "info"})` }} />
              <span className="wv-toast__message">{message}</span>
              <button type="button" className="wv-icon-btn wv-toast__close" aria-label="Dismiss notification" onClick={() => dismiss(id)} style={{ width: 28, height: 28 }}>
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </FeedbackContext.Provider>
  );
}

export function useWavyFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    // Fail-soft: unmounted/outside-provider usage still gets a console sink so
    // new component code never crashes because feedback is unavailable.
    const log = (message) => console.log("[WAVY feedback]", message);
    return { notify: log, success: log, error: log, warning: log, info: log, dismiss: () => { } };
  }
  return ctx;
}

const feedbackApi = { FeedbackProvider, useWavyFeedback };
export default feedbackApi;
